import { useVehicleCard } from "@/hooks/useVehicleCard";
import { toast } from "sonner";
import { getLanguage } from "@/utils/languageUtils";
import { vehicleCardUILabels, vehicleCardToastLabels } from "@/i18n";
import { ValueEstimationSection } from "../components/ValueEstimationSection";
import type { ValueEstimation, VehicleCardDetails } from "@/types/vehicleCard";
import { mapRatingIntervalsToCategories } from "@/admin/utils/ratingIntervals";
import { isMotorcycle } from "@/utils/vehicleType";
import { useEffect, useState } from "react";

interface VehicleCardValueEstimationProps {
  valueData?: ValueEstimation["valueData"];
  ratingIntervalsRaw?: ValueEstimation["ratingIntervalsRaw"];
  vehicleCardId: string;
  vehicleCard?: VehicleCardDetails;
}

export const VehicleCardValueEstimation = ({
  valueData,
  ratingIntervalsRaw,
  vehicleCardId,
  vehicleCard,
}: VehicleCardValueEstimationProps) => {
  const { fetchRatingIntervalsAsync, isFetchingRatingIntervals } =
    useVehicleCard();
  const [localPriceCategories, setLocalPriceCategories] = useState(
    mapRatingIntervalsToCategories(ratingIntervalsRaw),
  );

  useEffect(() => {
    setLocalPriceCategories(mapRatingIntervalsToCategories(ratingIntervalsRaw));
  }, [ratingIntervalsRaw]);

  const handleRefreshPrices = async () => {
    try {
      if (!vehicleCard?.vehicleDetails) {
        toast.error(vehicleCardUILabels(getLanguage()).vehicleDetailsMissing);
        return;
      }
      // AutoUncle does not price motorcycles. Without this the panel walks all six ladder
      // rungs and reports a misleading error; the backend guard is the backstop, but
      // skipping here also avoids a "värdering uppdaterad" toast that would be a lie.
      // Mirrors LeadValueEstimation - the two refresh panels must not diverge.
      if (isMotorcycle(vehicleCard.vehicleDetails.vehicleType)) {
        return;
      }

      // No request body: the backend rebuilds the AutoUncle request from the stored card row
      // (sync plan 2026-09-08), so this panel cannot drift from the card's own stored data.
      const response = await fetchRatingIntervalsAsync({ vehicleCardId });
      // Bands update immediately from the response; the headline price re-renders when
      // the invalidated vehicle-card query comes back (see useVehicleCard).
      setLocalPriceCategories(
        mapRatingIntervalsToCategories(response.ratingIntervals),
      );
      toast.success(
        vehicleCardToastLabels(getLanguage()).valueEstimationUpdated,
      );
    } catch (error) {
      toast.error(
        vehicleCardToastLabels(getLanguage()).valueEstimationUpdateError,
      );
    }
  };

  return (
    <ValueEstimationSection
      valueData={valueData}
      priceCategories={localPriceCategories}
      onRefreshPrices={handleRefreshPrices}
      isUpdating={isFetchingRatingIntervals}
      emptyMessage={vehicleCardUILabels(getLanguage()).noValueEstimationData}
    />
  );
};
